import { ApiError } from './fetchUtil'

export type ApiFailureKind = 'network' | 'auth' | 'forbidden' | 'validation' | 'server' | 'unknown'

export type ApiFailure = {
  kind: ApiFailureKind
  message: string
  status?: number
}

type BadRequestError = {
  errorCode?: string
  description?: string
  context?: string
  errorData1?: string
  errorData2?: string
}

type BadRequestResponse = {
  errors?: BadRequestError[]
}

function getNetworkErrorMessage(): string {
  return 'A network error occurred. Please check your connection and try again.'
}

function getUnknownErrorMessage(): string {
  return 'Sorry, something went wrong. Please try again.'
}

function getBadRequestErrorMessage(body: unknown, includeErrorCode: boolean): string | null {
  const badRequestBody = body as BadRequestResponse | null
  const firstError = badRequestBody?.errors?.[0]
  if (!firstError) {
    return null
  }

  return (
    firstError.errorData2 ||
    firstError.errorData1 ||
    firstError.description ||
    firstError.context ||
    (includeErrorCode ? firstError.errorCode : undefined) ||
    null
  )
}

export function classifyApiFailure(err: unknown): ApiFailure {
  if (err instanceof TypeError) {
    return {
      kind: 'network',
      message: getNetworkErrorMessage(),
    }
  }

  if (!(err instanceof ApiError)) {
    return {
      kind: 'unknown',
      message: getUnknownErrorMessage(),
    }
  }

  if (err.status === 401) {
    return {
      kind: 'auth',
      status: err.status,
      message: 'Your session has expired. Please sign in again and try again.',
    }
  }

  if (err.status === 403) {
    return {
      kind: 'forbidden',
      status: err.status,
      message:
        'You do not have permission to perform this action. Please contact your administrator.',
    }
  }

  if (err.status === 400) {
    return {
      kind: 'validation',
      status: err.status,
      message: getValidationErrorMessage(err) ?? 'The request could not be completed.',
    }
  }

  if (err.status >= 500) {
    return {
      kind: 'server',
      status: err.status,
      message: 'The server could not complete the request. Please try again.',
    }
  }

  return {
    kind: 'unknown',
    status: err.status,
    message: getUnknownErrorMessage(),
  }
}

export function getApiErrorMessage(err: unknown): string {
  return classifyApiFailure(err).message
}

export function getValidationErrorMessage(err: unknown): string | null {
  if (err instanceof TypeError) {
    return getNetworkErrorMessage()
  }

  if (!(err instanceof ApiError) || err.status !== 400) {
    return null
  }

  return getBadRequestErrorMessage(err.body, true)
}

type ProblemDetailsResponse = {
  title?: string
  detail?: string
  ErrorCode?: string
  ErrorData1?: string
  ErrorData2?: string
}

export function getOrderErrorMessage(err: unknown): string | null {
  if (err instanceof TypeError) {
    return getNetworkErrorMessage()
  }

  if (!(err instanceof ApiError)) {
    return null
  }

  if (err.status === 403) {
    return 'You do not have permission to place or update orders. Please contact your administrator.'
  }

  if (err.status !== 400) {
    return null
  }

  const body = err.body as (BadRequestResponse & ProblemDetailsResponse) | null
  if (!body) {
    return null
  }

  // ProblemDetails format (from /api/order)
  if (body.ErrorData2 || body.ErrorData1 || body.title) {
    return body.ErrorData2 || body.ErrorData1 || body.title || null
  }

  // BadRequestResponse format (from /kds/cart/validate)
  return getBadRequestErrorMessage(body, false)
}
